import { useGetInvoiceBoV1 } from "@/api/invoice";
import { Modal } from "@/components/ui/customs/modal";
import { useMemo, useState } from "react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { SelectV2 } from "@/components/ui/customs/select-v2";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { InvoiceFormat, useDownloadInvoicesZip } from "@/hooks/useDownloadInvoicesZip";
import { MONTHS_OPTIONS } from "@/constants/options/months.option";
import { useTranslation } from "react-i18next";

interface InvoicesDownloadPdfProps {
  closeModal: () => void;
}

const monthOptions = MONTHS_OPTIONS.map(({ label, value }) => ({
  label,
  value: value.toString(),
}));

const yearOptions = Array.from({ length: 6 }, (_, i) => {
  const y = new Date().getFullYear() - i;
  return { label: y.toString(), value: y.toString() };
});

const getPreviousMonth = () => {
  const now = new Date();
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return { year: prev.getFullYear(), month: prev.getMonth() };
};

export const InvoicesDownloadPdf = ({ closeModal }: InvoicesDownloadPdfProps) => {
  const { t } = useTranslation(["boInvoices", "common"]);
  const initial = useMemo(getPreviousMonth, []);
  const [year, setYear] = useState<number | undefined>(initial.year);
  const [month, setMonth] = useState<number | undefined>(initial.month);
  const [format, setFormat] = useState<InvoiceFormat>("pdf");

  const { data: invoices, isLoading: isLoadingInvoices } = useGetInvoiceBoV1({
    year,
    month: month !== undefined ? [month] : undefined,
  });

  const { download, isDownloading } = useDownloadInvoicesZip();

  const handleDownloadAll = () => {
    if (!invoices?.data) return;
    const keyField = format === "pdf" ? "invoicePDFKey" : "invoiceKey";
    const keys = invoices.data.map((inv) => inv[keyField]).filter((key): key is string => !!key);
    return download(keys, format);
  };

  const formatLabel = format === "pdf" ? "PDF" : "Excel";
  const downloadDisabled = isLoadingInvoices || isDownloading || !invoices?.data?.length;

  return (
    <Modal
      open={true}
      onClose={closeModal}
      contentClassName="max-w-[600px]"
      title={t("downloadModal.title")}
      footer={
        <>
          <Button onClick={handleDownloadAll} className="w-full" disabled={downloadDisabled}>
            {isDownloading
              ? t("downloadModal.downloading")
              : t("downloadModal.downloadAll", { format: formatLabel })}
          </Button>
          <Button variant="outline" onClick={closeModal} className="w-full">
            {t("common:actions.cancel")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col items-center gap-4">
        <SelectV2
          placeholder={t("downloadModal.yearPlaceholder")}
          options={yearOptions}
          value={year?.toString() ?? ""}
          onChange={(value) => setYear(value === "" ? undefined : Number(value))}
          clearable
          wrapperClassName="w-full"
        />
        <SelectV2
          placeholder={t("downloadModal.monthPlaceholder")}
          options={monthOptions}
          value={month?.toString() ?? ""}
          onChange={(value) => setMonth(value === "" ? undefined : Number(value))}
          clearable
          wrapperClassName="w-full"
        />
        <Label>{t("downloadModal.formatLabel")}</Label>
        <RadioGroup
          value={format}
          onValueChange={(value) => setFormat(value as InvoiceFormat)}
          className="flex gap-6"
        >
          <div className="flex items-center gap-2">
            <RadioGroupItem id="fmt-pdf" value="pdf" />
            <Label htmlFor="fmt-pdf">PDF</Label>
          </div>
          <div className="flex items-center gap-2">
            <RadioGroupItem id="fmt-excel" value="excel" />
            <Label htmlFor="fmt-excel">Excel</Label>
          </div>
        </RadioGroup>
      </div>
    </Modal>
  );
};
